import { MouseEvent, ReactNode, useCallback } from "react";

import classNames from "classnames";

import { EmptyArray } from "../../../utilities/EmptyArray";
import { BaseColumnProps } from "../BaseColumnProps";
import { useTableStyles } from "../TableStylesContext";
import { Action, ActionProps } from "./Action";
import { ActionButton } from "./ActionsInternal/ActionButton";
import { ActionMenu } from "./ActionsInternal/ActionMenu";
import { getActions } from "./ActionsInternal/helpers";
import { Custom } from "./Custom";

import styles from "./styles.module.css";

/** The `Actions` component properties. */
export interface ActionsProps extends Omit<BaseColumnProps, "align"> {
  /** The set of actions. */
  readonly children?: ReactNode;

  /** The minimum number of actions to display before showing the menu. */
  readonly minActions?: number;
  /** Whether to force all actions to be displayed in the menu. */
  readonly forceAllItemsToMenu?: boolean;
}

/** Column which displays a series of actions. */
export const Actions = (props: ActionsProps) => {
  const { children, minActions = 4, forceAllItemsToMenu = false } = props;
  const { columnTypeStyles } = useTableStyles();
  const actions = getActions(children);

  const clickHandler = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  return (
    <Custom {...props}>
      {(_, id) => {
        const filteredActions = actions.filter((action) => action.visible?.(id) ?? true);
        let visibleActions: ReadonlyArray<ActionProps> = filteredActions.slice(0, minActions);
        let menuActions: ReadonlyArray<ActionProps> = filteredActions.slice(minActions);

        if (forceAllItemsToMenu) {
          visibleActions = EmptyArray;
          menuActions = filteredActions;
        } else if (minActions < 0 || menuActions.length === 1) {
          visibleActions = filteredActions;
          menuActions = EmptyArray;
        }
        return (
          <div className={columnTypeStyles.ActionsContainer} onClick={clickHandler}>
            <div className={columnTypeStyles.Actions}>
              {visibleActions.map((props, index) => (
                <ActionButton {...props} key={index} id={id} />
              ))}
              {menuActions.length > 0 && <ActionMenu id={id} items={menuActions} />}
            </div>
          </div>
        );
      }}
    </Custom>
  );
};

/** Represents a single action which can be placed within the `Actions` column component. */
Actions.Action = Action;

/** The CSS class name applied to cells that get rendered with actions in. */
Actions.className = classNames(styles.ActionsCell, "actions");
Actions.headerAlignment = "center";
